import React, { useState } from 'react';
import { Select, MenuItem, FormControl, Tooltip, Box, IconButton, ClickAwayListener, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrekkingIconWhite from '../../assets/bike_profile_icons/BikeProfile_Trekking_white.svg';
import TrekkingIconDark from '../../assets/bike_profile_icons/BikeProfile_Trekking_dark.svg';
import RoadBikeIconWhite from '../../assets/bike_profile_icons/BikeProfile_RoadBike_white.svg';
import RoadBikeIconDark from '../../assets/bike_profile_icons/BikeProfile_RoadBike_dark.svg';
import GravelBikeIconWhite from '../../assets/bike_profile_icons/BikeProfile_GravelBike_white.svg';
import GravelBikeIconDark from '../../assets/bike_profile_icons/BikeProfile_GravelBike_dark.svg';
import MountainBikeIconWhite from '../../assets/bike_profile_icons/BikeProfile_MountainBike_white.svg';
import MountainBikeIconDark from '../../assets/bike_profile_icons/BikeProfile_MountainBike_dark.svg';
import './RouteProfileSelector.css';

export type RouteProfile = 'trekking' | 'road' | 'gravel' | 'mtb';

// Default profile - can be configured here
// Available options: 'trekking', 'road', 'gravel', 'mtb'
export const DEFAULT_ROUTE_PROFILE: RouteProfile = 'road';

interface RouteProfileSelectorProps {
  selectedProfile: RouteProfile;
  onProfileChange: (profile: RouteProfile) => void;
  variant?: 'default' | 'bar'; // New prop to control layout variant
}

const profiles = [
  {
    value: 'road',
    label: 'Road Bike',
    description: 'Optimized for paved roads and speed. Avoids unpaved surfaces and prioritizes smooth asphalt. Best for road cycling and training rides.',
    icon: <img src={RoadBikeIconWhite} alt="Road Bike" style={{ height: 38, aspectRatio: 'auto' }} />,
    menuIcon: <img src={RoadBikeIconDark} alt="Road Bike" style={{ height: 38, aspectRatio: 'auto' }} />
  },
  {
    value: 'gravel',
    label: 'Gravel Bike',
    description: 'Balanced for mixed surfaces. Handles both paved roads and gravel paths comfortably. Perfect for exploring backroads and gravel adventures.',
    icon: <img src={GravelBikeIconWhite} alt="Gravel Bike" style={{ height: 38, aspectRatio: 'auto' }} />,
    menuIcon: <img src={GravelBikeIconDark} alt="Gravel Bike" style={{ height: 38, aspectRatio: 'auto' }} />
  },
  {
    value: 'trekking',
    label: 'Trekking Bike',
    description: 'Versatile touring profile. Balances efficiency with comfort, avoids traffic while staying on reasonable surfaces. Ideal for bike touring and casual rides.',
    icon: <img src={TrekkingIconWhite} alt="Trekking" style={{ height: 38, aspectRatio: 'auto' }} />,
    menuIcon: <img src={TrekkingIconDark} alt="Trekking" style={{ height: 38, aspectRatio: 'auto' }} />
  },
  {
    value: 'mtb',
    label: 'Mountain Bike',
    description: 'Designed for trails and technical terrain. Prefers paths and tracks over paved roads. Best for off-road adventures and singletrack riding.',
    icon: <img src={MountainBikeIconWhite} alt="Mountain Bike" style={{ height: 38, aspectRatio: 'auto' }} />,
    menuIcon: <img src={MountainBikeIconDark} alt="Mountain Bike" style={{ height: 38, aspectRatio: 'auto' }} />
  }
] as const;

export const RouteProfileSelector: React.FC<RouteProfileSelectorProps> = ({
  selectedProfile,
  onProfileChange,
  variant = 'default'
}) => {
  const currentProfile = profiles.find(p => p.value === selectedProfile);
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  
  // Detect landscape orientation on mobile/tablet devices
  const isSmallScreen = useMediaQuery('(max-width: 900px)');
  const isShortScreen = useMediaQuery('(max-height: 600px)'); // Landscape mobile typically has limited height
  const isOrientationLandscape = useMediaQuery('(orientation: landscape)');
  const isLandscape = (isSmallScreen || isShortScreen) && isOrientationLandscape;

  const handleTooltipToggle = (profileValue: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenTooltip(openTooltip === profileValue ? null : profileValue);
  };

  const handleTooltipClose = (event: any) => {
    // Don't close if clicking on an info button
    const target = event.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    setOpenTooltip(null);
  };

  const selectorElement = (
    <FormControl size="small" sx={{ 
      minWidth: 120,
      // Make selector more compact in landscape mode
      '& .MuiInputBase-root': {
        height: isLandscape ? '32px' : 'auto',
        minHeight: isLandscape ? '32px' : 'auto'
      }
    }}>
      <Select
        value={selectedProfile}
        onChange={(e) => onProfileChange(e.target.value as RouteProfile)}
        displayEmpty
        className="route-profile-select"
        sx={{
          // Reduce padding in landscape mode
          py: isLandscape ? 0 : undefined,
          '& .MuiSelect-select': {
            py: isLandscape ? '4px' : undefined,
            fontSize: isLandscape ? '0.75rem' : '0.875rem'
          }
        }}
        MenuProps={{
            PaperProps: {
              sx: {
                marginTop: '4px',
                maxWidth: 'calc(100vw - 20px)', // Prevent overflow beyond viewport with spacing
                right: '10px !important', // Force spacing from right edge
                left: 'auto !important',
              }
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
          }}
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isLandscape ? 0.5 : 1, justifyContent: 'flex-end' }}>
              <span style={{ fontSize: isLandscape ? '0.75rem' : '0.875rem' }}>{currentProfile?.label}</span>
              <Box sx={{ 
                height: isLandscape ? 28 : 38,
                display: 'flex',
                alignItems: 'center'
              }}>
                {React.cloneElement(currentProfile?.icon as React.ReactElement, {
                  style: { height: isLandscape ? 28 : 38, aspectRatio: 'auto' }
                })}
              </Box>
            </Box>
          )}
        >
          {profiles.map((profile) => (
            <MenuItem 
              key={profile.value}
              value={profile.value}
              className="route-profile-menu-item"
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                width: '100%',
                justifyContent: 'flex-end'
              }}>
                <span>{profile.label}</span>
                {profile.menuIcon}
                <ClickAwayListener onClickAway={handleTooltipClose}>
                  <div>
                    <Tooltip 
                      title={profile.description}
                      arrow
                      placement="left"
                      open={openTooltip === profile.value}
                      onClose={handleTooltipClose}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -10],
                              },
                            },
                            {
                              name: 'preventOverflow',
                              options: {
                                boundary: 'viewport',
                                padding: 8,
                              },
                            },
                          ],
                        },
                      }}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: '280px',
                          fontSize: '0.813rem'
                        }
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={handleTooltipToggle(profile.value)}
                        sx={{ 
                          padding: '1px',
                          minWidth: '16px',
                          minHeight: '16px',
                          color: 'text.disabled',
                          opacity: 0.25,
                          transition: 'opacity 0.2s',
                          '&:hover': {
                            opacity: 0.6,
                            backgroundColor: 'transparent'
                          }
                        }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: '12px' }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </ClickAwayListener>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
  );

  // If variant is 'bar', render the full bar layout
  if (variant === 'bar') {
    return (
      <div className="route-profile-selector-bar">
        <span className="route-profile-selector-label">
          Bike Type:
        </span>
        {selectorElement}
      </div>
    );
  }

  // Default variant - just the selector
  return selectorElement;
};
